import { useEffect } from "react";

export function useScrollSpy(
  selectors: string[],
  root: HTMLElement | null | undefined,
  onChange: (id: string) => void,
  offset: number = 50,
) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const headingsRef = { current: [] as HTMLElement[] };
    const container = root ?? document;

    const updateHeadings = () => {
      headingsRef.current = Array.from(
        container.querySelectorAll<HTMLElement>(selectors.join(",")),
      );
    };

    const findActive = () => {
      const headings = headingsRef.current;
      if (!headings.length) return;

      const scrollTop = root ? root.scrollTop : window.scrollY;
      const viewportHeight = root ? root.clientHeight : window.innerHeight;
      const scrollHeight = root
        ? root.scrollHeight
        : document.documentElement.scrollHeight;

      let newActiveId = "";

      if (scrollTop <= offset) {
        newActiveId = headings[0]?.id ?? "";
      } else if (scrollTop + viewportHeight >= scrollHeight - offset) {
        newActiveId = headings[headings.length - 1]?.id ?? "";
      } else {
        let closest = { id: "", topDiff: Infinity };
        headings.forEach((h) => {
          const rect = h.getBoundingClientRect();
          const diff = Math.abs(rect.top - offset);
          if (diff < closest.topDiff) closest = { id: h.id, topDiff: diff };
        });
        newActiveId = closest.id;
      }

      if (newActiveId) onChange(newActiveId);
    };

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          findActive();
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleResize = () => {
      updateHeadings();
      findActive();
    };

    updateHeadings();
    findActive();

    const target = root ?? window;
    target.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);

    return () => {
      target.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", handleResize);
    };
  }, [selectors, root, offset, onChange]);
}
